import { type Icon } from "@phosphor-icons/react";
import type { DateRangeValue } from "@/components/admin/DateRangePicker";
import { cn } from "@/lib/utils";

export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function defaultRange(): DateRangeValue {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: formatLocalDate(from), to: formatLocalDate(to) };
}

export function km(value: number): string {
  return `${value.toLocaleString("bs-BA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KM`;
}

export function shortDate(value: string): string {
  return new Intl.DateTimeFormat("bs-BA", { day: "2-digit", month: "short" }).format(
    new Date(`${value}T00:00:00`),
  );
}

export function Metric({
  label,
  value,
  detail,
  icon: IconCmp,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail: string;
  icon: Icon;
  tone?: "neutral" | "primary";
}) {
  return (
    <div
      className={cn(
        "flex min-h-28 flex-col justify-between rounded-xl border px-4 py-3.5 shadow-sm",
        tone === "primary" ? "border-primary/20 bg-primary/10" : "border-border/60 bg-card",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={cn(
            "flex size-9 items-center justify-center rounded-lg",
            tone === "primary" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
          )}
        >
          <IconCmp weight="duotone" />
        </span>
        <span className="text-[11px] font-medium uppercase text-muted-foreground">{label}</span>
      </div>
      <div>
        <p className="text-2xl font-semibold tabular-nums text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          {value}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}

export function LoadingState() {
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
      <div className="h-80 animate-pulse rounded-xl border border-border/60 bg-card" />
      <div className="h-80 animate-pulse rounded-xl border border-border/60 bg-card" />
    </div>
  );
}

export function ErrorCard({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
      {message}
    </div>
  );
}
